import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { db } from '../../firebase.ts';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import Icon from '../UI/Icon';
import sprechheldenHeld from '../../assets/sprechheldenHeld.png';
import './WelcomeModal.css';

function WelcomeModal({ show, onClose, onTestStart, userData }) {
  const [dashboardVideo, setDashboardVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);

  // Check if user has monthly_promo plan (games-only access)
  const hasGamesOnlyAccess = () => {
    if (!userData || !userData.subscription) return false;
    return userData.subscription.planKey === 'monthly_promo' ||
           userData.subscription.type === 'monthly_promo';
  };
  const features = [
    {
      id: 1,
      title: 'Lernmodule',
      description: 'Strukturierte Lerneinheiten mit gezielten Übungen',
      icon: 'modules',
      color: '#B2D8D8'
    },
    {
      id: 2,
      title: 'Spiele',
      description: 'Zwillingspaare finden, Geschichten und Sprechhelden auf Lautsuche zum spielerischen Lernen',
      icon: 'games',
      color: '#FFB7B2'
    },
    {
      id: 3,
      title: 'Lernmaterialien',
      description: 'Arbeitsblätter und Bildkarten zum Ausdrucken',
      icon: 'materials',
      color: '#FFF3CD'
    }
  ];

  // Fetch dashboard video from Firestore
  useEffect(() => {
    const fetchDashboardVideo = async () => {
      try {
        const videoQuery = query(
          collection(db, 'dashboard_video'),
          where('isActive', '==', true),
          limit(1)
        );
        const querySnapshot = await getDocs(videoQuery);

        if (!querySnapshot.empty) {
          const videoDoc = querySnapshot.docs[0];
          setDashboardVideo({
            id: videoDoc.id,
            ...videoDoc.data()
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard video:', error);
      }
    };

    if (show) {
      fetchDashboardVideo();
    }
  }, [show]);

  const handleVideoClick = () => {
    setIsPlaying(true);
  };

  const handleVideoError = () => {
    setHasVideoError(true);
    console.error('Error loading video from Firebase Storage');
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      className="welcome-modal"
    >
      <Modal.Header className="welcome-modal-header">
        <div className="text-center w-100">
          <img
            src={sprechheldenHeld}
            alt="Sprechhelden Hero"
            className="welcome-hero-image mb-3"
          />
          <h2 className="welcome-title mb-2">
            Willkommen bei Sprechhelden!
          </h2>
          <p className="welcome-subtitle mb-0">
            Deine Plattform für logopädische Förderung
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="welcome-modal-body">
        <div className="welcome-intro mb-4">
          <p className="text-center">
            <strong>Sprechhelden</strong> unterstützt bei der logopädischen Förderung
            von Kindern mit gezielten Übungen, spielerischen Elementen und hochwertigen Materialien.
          </p>
          <p className="text-center mb-3">
            Dafür gibt es verschiedene Formate auf Sprechhelden:
          </p>
          <div className="formats-container">
            {features.map((feature) => (
              <div key={feature.id} className="format-item">
                <div
                  className="format-icon"
                  style={{ backgroundColor: feature.color }}
                >
                  <Icon name={feature.icon} size="sm" />
                </div>
                <div className="format-text">
                  <strong>{feature.title}:</strong> {feature.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Video Section */}
        {dashboardVideo && dashboardVideo.isActive && !hasVideoError && (
          <div className="welcome-video-section mb-4">
            <div className="video-wrapper-modal">
              {!isPlaying && dashboardVideo.thumbnailUrl && (
                <div
                  className="video-thumbnail-overlay-modal"
                  onClick={handleVideoClick}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={dashboardVideo.thumbnailUrl}
                    alt={dashboardVideo.title || 'Video'}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="play-button-overlay-modal">
                    <div className="play-button-modal">
                      <Icon name="play" size="xl" />
                    </div>
                  </div>
                </div>
              )}

              <video
                className="w-100"
                controls={isPlaying}
                onError={handleVideoError}
                style={{
                  display: (!isPlaying && dashboardVideo.thumbnailUrl) ? 'none' : 'block',
                  maxHeight: '400px',
                  borderRadius: '8px'
                }}
                onClick={handleVideoClick}
              >
                <source src={dashboardVideo.videoUrl} type="video/mp4" />
                <source src={dashboardVideo.videoUrl} type="video/webm" />
                Ihr Browser unterstützt das Video-Tag nicht.
              </video>

              {!isPlaying && !dashboardVideo.thumbnailUrl && (
                <div
                  className="video-click-overlay-modal position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  onClick={handleVideoClick}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 10,
                    borderRadius: '8px'
                  }}
                >
                  <div className="play-button-modal">
                    <Icon name="play" size="xl" />
                  </div>
                </div>
              )}
            </div>
            {dashboardVideo.title && (
              <p className="text-center mt-2 mb-0">
                <strong>{dashboardVideo.title}</strong>
              </p>
            )}
            <p className="text-center mt-2 mb-0" style={{
              fontSize: '11px',
              color: '#6c757d',
              fontStyle: 'italic'
            }}>
              © Sprechhelden – Dieses Video und alle enthaltenen Inhalte sind urheberrechtlich geschützt.
            </p>
          </div>
        )}

        {!hasGamesOnlyAccess() && (
          <div className="welcome-closing text-center mt-4">
            <p className="mb-2">
              Im ersten Schritt werden wir einen Test durchführen, um dein Kind einzuordnen und die richtigen Laute zu üben. Nehmt euch dafür gemeinsam 10 Minuten Zeit.
            </p>
            <div className="test-disclaimer">
              <Icon name="info-circle" size="sm" className="me-2" />
              <span>
                <strong>Hinweis:</strong> Der Test kann nicht immer alles genau auswerten, da jedes Kind in seiner Aussprache individuell ist.
                Gerne können Sie auch einen online Termin mit einer unserer Logopäden vereinbaren.
              </span>
            </div>
          </div>
        )}

        {hasGamesOnlyAccess() && (
          <div className="welcome-closing text-center mt-4">
            <p className="mb-2">
              Viel Spaß beim Spielen! Entdecke verschiedene Spiele, die spielerisch das Sprechen fördern.
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="welcome-modal-footer">
        {!hasGamesOnlyAccess() ? (
          <div className="d-flex gap-3 align-items-center">
            <Button
              variant="outline-secondary"
              size="md"
              onClick={() => window.open('https://cal.com/sprechhelden/sprechhelden-personliche-beratung-fur-eltern-mitglieder', '_blank')}
              className="welcome-appointment-button"
            >
              <Icon name="calendar" size="sm" className="me-2" />
              Termin vereinbaren
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                onClose();
                if (onTestStart) {
                  onTestStart();
                }
              }}
              className="welcome-start-button"
            >
              <Icon name="play" size="sm" className="me-2" />
              Test starten
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-3 align-items-center justify-content-center w-100">
            <Button
              variant="primary"
              size="lg"
              onClick={onClose}
              className="welcome-start-button"
            >
              <Icon name="games" size="sm" className="me-2" />
              Los geht's!
            </Button>
          </div>
        )}
      </Modal.Footer>

      <style jsx>{`
        .welcome-video-section {
          max-width: 100%;
        }

        .video-wrapper-modal {
          position: relative;
          width: 100%;
          background-color: #f8f9fa;
          border-radius: 8px;
          overflow: hidden;
        }

        .video-thumbnail-overlay-modal {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          overflow: hidden;
          border-radius: 8px;
        }

        .video-thumbnail-overlay-modal img {
          position: absolute;
          top: 0;
          left: 0;
        }

        .play-button-overlay-modal {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(0, 0, 0, 0.3);
          transition: background-color 0.3s ease;
        }

        .video-thumbnail-overlay-modal:hover .play-button-overlay-modal {
          background-color: rgba(0, 0, 0, 0.5);
        }

        .play-button-modal {
          width: 70px;
          height: 70px;
          background-color: rgba(255, 255, 255, 0.95);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease, background-color 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .play-button-modal:hover {
          transform: scale(1.1);
          background-color: #fff;
        }

        @media (max-width: 768px) {
          .play-button-modal {
            width: 50px;
            height: 50px;
          }
        }
      `}</style>
    </Modal>
  );
}

export default WelcomeModal;